import { useApolloClient } from "@apollo/client";
import { getDataOrThrow } from "@msys/common";
import { ModalOpenButton } from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Edit as EditIcon } from "@mui/icons-material";
import { Divider, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  DocActorModifyInput,
  DocActorType,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import { CrmCompanySelectModal } from "../crm-companies/modals/CrmCompanySelectModal.js";
import { DocumentActorData } from "../documents/DocumentActorData.js";
import { DocumentActorEditModal } from "../documents/DocumentActorEditModal.js";
import { DocumentActorsModal } from "../documents/DocumentActorsModal.js";
import { useActorTypes } from "../documents/useActorTypes.js";
import {
  useAddRequirementDocumentActorMutation,
  useModifyRequirementDocumentActorMutation,
  useRequirementDocumentActorsSuspenseQuery,
  useResetRequirementDocumentActorMutation,
} from "./RequirementDocumentActorsModal.generated.js";

interface Props {
  handleClose: () => void;
  projectId: string;
  requirementId: string;
}

export const RequirementDocumentActorsModal = ({
  handleClose,
  projectId,
  requirementId,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const { actorTypeLabels } = useActorTypes();

  const client = useApolloClient();
  const query = useRequirementDocumentActorsSuspenseQuery({
    client,
    variables: { projectId, requirementId },
  });
  const [addActor] = useAddRequirementDocumentActorMutation({
    client,
    refetchQueries: [namedOperations.Query.RequirementDocumentActors],
    awaitRefetchQueries: true,
  });
  const [modifyActor] = useModifyRequirementDocumentActorMutation({
    client,
    refetchQueries: [namedOperations.Query.RequirementDocumentActors],
    awaitRefetchQueries: true,
  });
  const [resetActor] = useResetRequirementDocumentActorMutation({
    client,
    refetchQueries: [namedOperations.Query.RequirementDocumentActors],
    awaitRefetchQueries: true,
  });

  const requirement = getDataOrThrow(query.data!.requirement).requirement; // FIXME: why is data possibly undefined?
  if (!requirement) throw new Error("Requirement not found");

  const contractorActor = requirement?.contractor;
  const contracteeActor = requirement?.contractee;
  const clientActor = requirement?.client;

  const handleAdd = async (actorType: DocActorType, crmCompanyId: string) => {
    await addActor({
      variables: {
        input: {
          projectId,
          docId: requirementId,
          docActorType: actorType,
          crmCompanyId,
        },
      },
    });
  };

  const handleChange = async (
    actorId: string,
    actorType: DocActorType,
    actorInput: DocActorModifyInput,
    handleClose: () => void
  ) => {
    await modifyActor({
      variables: {
        input: {
          projectId,
          docId: requirementId,
          docActorId: actorId,
          docActorType: actorType,
          docActor: actorInput,
        },
      },
    });
    handleClose();
  };

  const handleReset = async (
    actorId: string,
    actorType: DocActorType,
    crmCompanyId: string
  ) => {
    await resetActor({
      variables: {
        input: {
          projectId,
          docId: requirementId,
          docActorId: actorId,
          docActorType: actorType,
          crmCompanyId,
        },
      },
    });
  };

  return (
    <DocumentActorsModal handleClose={handleClose}>
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CONTRACTEE"} actor={contracteeActor} />
        {contracteeActor ? (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={DocumentActorEditModal}
              modalProps={{
                actor: contracteeActor,
                title: t("{docActorType} details", {
                  ns: "Global",
                  docActorType: actorTypeLabels[contracteeActor.type],
                }),
                handleReset: async (crmCompanyId, handleClose) => {
                  await handleReset(
                    contracteeActor.id,
                    contracteeActor.type,
                    crmCompanyId
                  );
                  handleClose();
                },
                handleComplete: async (actorFormValues, handleClose) => {
                  await handleChange(
                    contracteeActor.id,
                    contracteeActor.type,
                    actorFormValues,
                    handleClose
                  );
                },
              }}
            >
              <IconButton color="secondary">
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        ) : (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={CrmCompanySelectModal}
              modalProps={{
                title: t("Select {docActorType}", {
                  ns: "Global",
                  docActorType: actorTypeLabels["CONTRACTEE"],
                }),
                inputLabel: t("Choose one of your contacts", {
                  ns: "Global",
                }),
                canCreateNew: true,
                createNewLabel: t("Create new contact", { ns: "Global" }),
                allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                required: true,
                handleComplete: async (crmCompanyId, handleClose) => {
                  await handleAdd("CONTRACTEE", crmCompanyId);
                  handleClose();
                },
              }}
            >
              <IconButton color="secondary">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        )}
      </Stack>
      <Divider />
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CLIENT"} actor={clientActor} />
        {clientActor ? (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={DocumentActorEditModal}
              modalProps={{
                actor: clientActor,
                title: t("{docActorType} details", {
                  ns: "Global",
                  docActorType: actorTypeLabels[clientActor.type],
                }),
                handleReset: async (crmCompanyId, handleClose) => {
                  await handleReset(
                    clientActor.id,
                    clientActor.type,
                    crmCompanyId
                  );
                  handleClose();
                },
                handleComplete: async (actorFormValues, handleClose) => {
                  await handleChange(
                    clientActor.id,
                    clientActor.type,
                    actorFormValues,
                    handleClose
                  );
                },
              }}
            >
              <IconButton color="secondary">
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        ) : (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={CrmCompanySelectModal}
              modalProps={{
                title: t("Select {docActorType}", {
                  ns: "Global",
                  docActorType: actorTypeLabels["CLIENT"],
                }),
                inputLabel: t("Choose one of your contacts", {
                  ns: "Global",
                }),
                canCreateNew: true,
                createNewLabel: t("Create new contact", { ns: "Global" }),
                allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                required: true,
                handleComplete: async (crmCompanyId, handleClose) => {
                  await handleAdd("CLIENT", crmCompanyId);
                  handleClose();
                },
              }}
            >
              <IconButton color="secondary">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        )}
      </Stack>
      <Divider />
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <DocumentActorData actorType={"CONTRACTOR"} actor={contractorActor} />
        {contractorActor ? (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={DocumentActorEditModal}
              modalProps={{
                actor: contractorActor,
                title: t("{docActorType} details", {
                  ns: "Global",
                  docActorType: actorTypeLabels[contractorActor.type],
                }),
                handleReset: async (crmCompanyId, handleClose) => {
                  await handleReset(
                    contractorActor.id,
                    contractorActor.type,
                    crmCompanyId
                  );
                  handleClose();
                },
                handleComplete: async (actorFormValues, handleClose) => {
                  await handleChange(
                    contractorActor.id,
                    contractorActor.type,
                    actorFormValues,
                    handleClose
                  );
                },
              }}
            >
              <IconButton color="secondary">
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        ) : (
          <RestrictedByDocumentPermissionWithDebug
            permission="requirement_actors_update"
            document={requirement}
          >
            <ModalOpenButton
              Modal={CrmCompanySelectModal}
              modalProps={{
                title: t("Select {docActorType}", {
                  ns: "Global",
                  docActorType: actorTypeLabels["CONTRACTOR"],
                }),
                inputLabel: t("Choose one of your contacts", {
                  ns: "Global",
                }),
                canCreateNew: true,
                createNewLabel: t("Create new contact", { ns: "Global" }),
                allowedContactTypesForCreate: ["COMPANY", "INDIVIDUAL"],
                required: true,
                handleComplete: async (crmCompanyId, handleClose) => {
                  await handleAdd("CONTRACTOR", crmCompanyId);
                  handleClose();
                },
              }}
            >
              <IconButton color="secondary">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          </RestrictedByDocumentPermissionWithDebug>
        )}
      </Stack>
    </DocumentActorsModal>
  );
};
